import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CompanyOnboardingService } from '@edtech-ui/hiring-workspace/services/company-onboarding.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { CompanyProfile } from '@edtech-ui/core/models/onBord-company/company-profile';
import { ToastrService } from 'ngx-toastr';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from 'environments/environment';
import { INDIAN_LOCATIONS } from '@edtech-ui/core/constants/recuritment-constants/indian-locations.constants';
import { StudentJobApplicantNameValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-name';
import { StudentJobEmailValidator } from '@edtech-ui/core/utils/student-apply-job/student-job-email';
import { companyAboutValidator, urlValidator } from '@edtech-ui/core/utils/recruitment-utils/company-profile-validators';

@Component({
	selector: 'app-company-profile',
	standalone: true,
	imports: [CommonModule, FormsModule, MatProgressSpinnerModule, ReactiveFormsModule],
	templateUrl: './company-profile.component.html',
	styleUrls: ['./company-profile.component.scss']
})
export class CompanyProfileComponent implements OnInit {

	isEditMode = false;
	private backupData: any = {};
	companyProfile!: CompanyProfile;
	companyProfileForm!: FormGroup;
	isLoading = false;
	selectedLogoFile: File | null = null;
	indianLocations = INDIAN_LOCATIONS;
	activeLocationIndex: number | null = null;
	filteredOfficeLocations: string[] = [];
	showOfficeLocationsDropdown = false;

	getLogoUrl(url: string | null | undefined): string {
		if (!url) return '';
		if (url.startsWith('http') || url.startsWith('data:')) {
			return url;
		}
		const baseUrl = environment.companyApiUrl;
		return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
	}


	constructor(
		private router: Router,
		private _location: Location,
		private companyService: CompanyOnboardingService,
		private toastr: ToastrService
	) {
		this.companyProfileForm = new FormGroup({
			company_name: new FormControl('', [Validators.required, StudentJobApplicantNameValidator()]),
			company_url: new FormControl('', urlValidator()),
			industry: new FormControl('', [Validators.required, StudentJobApplicantNameValidator()]),
			linkedin_profile: new FormControl('', urlValidator()),
			about_company: new FormControl('', companyAboutValidator()),
			logo_url: new FormControl(''),
			default_passing_score: new FormControl(null, [Validators.min(1), Validators.max(100)]),
			office_locations: new FormArray([]),
			contact: new FormGroup({
				poc_name: new FormControl(''),
				poc_email: new FormControl('', Validators.email),
				poc_phone: new FormControl(''),
				official_email: new FormControl('', [Validators.required, Validators.email, StudentJobEmailValidator()]),
				hr_name: new FormControl(''),
				hr_email: new FormControl('', Validators.email),
				hr_phone: new FormControl('')
			})
		});
	}


	ngOnInit(): void {
		this.getCompanyProfile();
	}


	getCompanyProfile() {
		this.isLoading = true;
		this.companyService.getCompanyProfile().pipe(
			tap(response => {
				if (response.Success) {
					this.companyProfile = response.Data;
					this.patchCompanyProfile(response.Data);
				} else {
					this.toastr.error(response.Message);
				}
			}),
			finalize(() => {
				this.isLoading = false;
			}),
			catchError(() => {
				this.toastr.error('Something went wrong . Please try again');
				return of(null);
			})
		).subscribe()
	}


	updateCompanyProfile(): void {
		if (this.companyProfileForm.invalid) {
			this.companyProfileForm.markAllAsTouched();
			return;
		}
		const formValue = this.companyProfileForm.value;
		const formData = new FormData();
		formData.append('company_name', formValue.company_name || '');
		formData.append('company_url', formValue.company_url || '');
		formData.append('industry', formValue.industry || '');
		formData.append('linkedin_profile', formValue.linkedin_profile || '');
		formData.append('about_company', formValue.about_company || '');
		if (formValue.default_passing_score != null) {
			formData.append('default_passing_score', formValue.default_passing_score.toString());
		}
		this.officeLocations.controls.forEach((control) => {
			formData.append('office_locations', control.value);
		});
		formData.append('poc_name', formValue.contact.poc_name || '');
		formData.append('poc_email', formValue.contact.poc_email || '');
		formData.append('poc_phone', formValue.contact.poc_phone || '');
		formData.append('official_email', formValue.contact.official_email || '');
		formData.append('hr_name', formValue.contact.hr_name || '');
		formData.append('hr_email', formValue.contact.hr_email || '');
		formData.append('hr_phone', formValue.contact.hr_phone || '');

		if (this.selectedLogoFile) {
			formData.append('logo', this.selectedLogoFile);
		}
		this.isLoading = true;
		this.companyService.putCompanyProfile(formData, this.companyProfile.company_id).pipe(tap(response => {
			if (response.Success) {
				this.toastr.success('Company profile updated successfully');
				this.companyProfile = { ...this.companyProfile, ...formValue };
				this.isEditMode = false;
				this.companyService.getCompanyProfile().subscribe();
			} else {
				this.toastr.error(response.Message);
			}
		}),
			finalize(() => {
				this.isLoading = false;
			}),
			catchError(() => {
				this.toastr.error('Failed to update company profile');
				return of(null);
			})
		).subscribe();
	}


	patchCompanyProfile(data: CompanyProfile): void {
		this.officeLocations.clear();
		data.office_locations?.forEach(location => {
			this.officeLocations.push(
				new FormControl(location)
			);
		});

		this.companyProfileForm.patchValue({
			company_name: data.company_name,
			company_url: data.company_url,
			industry: data.industry,
			linkedin_profile: data.linkedin_profile,
			about_company: data.about_company,
			logo_url: data.logo_url,
			default_passing_score: data.default_passing_score,
			contact: {
				poc_name: data.contact?.poc_name,
				poc_email: data.contact?.poc_email,
				poc_phone: data.contact?.poc_phone,
				official_email: data.contact?.official_email,
				hr_name: data.contact?.hr_name,
				hr_email: data.contact?.hr_email,
				hr_phone: data.contact?.hr_phone
			}
		});
	}


	get officeLocations(): FormArray {
		return this.companyProfileForm.get('office_locations') as FormArray;
	}


	goBack(): void {
		this._location.back();
	}


	toggleEditMode(): void {
		this.isEditMode = true;
	}


	cancelEdit(): void {
		this.patchCompanyProfile(this.companyProfile);
		this.isEditMode = false;

	}

	saveProfile(): void {
		this.updateCompanyProfile();
	}

	addLocation(): void {
		this.officeLocations.push(new FormControl(''));
	}

	removeLocation(index: number): void {
		this.officeLocations.removeAt(index);
	}


	triggerFileInput(fileInput: HTMLInputElement): void {
		fileInput.click();
	}


	onLogoSelected(event: any): void {
		const file = event.target.files[0];

		if (file) {
			this.selectedLogoFile = file;

			const reader = new FileReader();

			reader.onload = (e: any) => {
				this.companyProfileForm.patchValue({
					logo_url: e.target.result
				});
			};

			reader.readAsDataURL(file);
		}
	}

	removeLogo(): void {
		this.companyProfileForm.patchValue({
			logo_url: null
		});
	}


	trackByIndex(index: number, obj: any): any {
		return index;
	}


	onOfficeLocationInput(event: Event, index: number): void {
		const value = (event.target as HTMLInputElement).value;
		this.activeLocationIndex = index;
		if (value && value.trim().length >= 1) {
			const query = value.toLowerCase();
			this.filteredOfficeLocations = this.indianLocations.filter(loc =>
				loc.toLowerCase().includes(query)
			);
			this.showOfficeLocationsDropdown = true;
		} else {
			this.filteredOfficeLocations = [];
			this.showOfficeLocationsDropdown = false;
		}
	}


	onOfficeLocationFocus(event: Event, index: number): void {
		const value = (event.target as HTMLInputElement).value;
		this.activeLocationIndex = index;
		if (value && value.trim().length >= 1) {
			const query = value.toLowerCase();
			this.filteredOfficeLocations = this.indianLocations.filter(loc =>
				loc.toLowerCase().includes(query)
			);
			this.showOfficeLocationsDropdown = true;
		} else {
			this.filteredOfficeLocations = [];
			this.showOfficeLocationsDropdown = false;
		}
	}


	onOfficeLocationBlur(index: number): void {
		if (this.activeLocationIndex === index) {
			this.showOfficeLocationsDropdown = false;
		}
	}


	selectOfficeLocation(loc: string, index: number): void {
		const control = this.officeLocations.at(index);
		if (control) {
			control.setValue(loc);
		}
		this.showOfficeLocationsDropdown = false;
		this.activeLocationIndex = null;
	}

	
}
